import { memo, useCallback } from 'react';

import Text from '@ui/text/text/text';
import OptionalComponent from '@ui/optional-component/optional-component';
import { GreenroomUserProfile } from '@/types/greenroom/profile.types';
import TooltipPortal from '@ui/tooltip-portal/tooltip-portal';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import Button from '@ui/buttons/button/button';
import InputWithOverlay from '@ui/input-with-overlay/input-with-overlay';

type TEditEmailProps = {
  email: string | undefined;
  onChange: (value: string) => void;
  greenroomProfile: GreenroomUserProfile | undefined;
  validEmail: boolean;
  setOpenInviteModal: (open: boolean) => void;
  savingCast: boolean;
  invitingCast: boolean;
};

const EditEmail = memo(
  ({
    validEmail,
    email,
    onChange,
    greenroomProfile,
    setOpenInviteModal,
    savingCast,
    invitingCast,
  }: TEditEmailProps) => {
    const renderLabel = useCallback(() => {
      return (
        <Text design="GlobalIcoText3" className="L2 margin-l-16 margin-b-4 flex ai-center gap-4">
          Email{' '}
          <OptionalComponent display={!!greenroomProfile}>
            <Text tag="span" design="GlobalIcoText2">
              *
            </Text>
          </OptionalComponent>
          <TooltipPortal
            styles={{ padding: '8px' }}
            tooltipContent={
              <Text color="var(--primitives-mode-1-gray-100)" className="P2">
                Required if they'll be joining as a remote presenter in Greenroom
              </Text>
            }
            childrenWrapperClass="flex"
          >
            <Icon name={ICONS.INFO_CIRCLE} size={10} />
          </TooltipPortal>
        </Text>
      );
    }, [greenroomProfile]);

    return (
      <label htmlFor="email" className="flex-column gap-4">
        <InputWithOverlay
          jsxLabel={renderLabel}
          inputProps={{
            id: 'email',
            value: email || '',
            onChange: (e) => onChange(e.target.value),
            placeholder: 'Add email',
            disabled: !!greenroomProfile,
            type: 'email',
          }}
          padRightInnerContent
          innerContentClassName="flex ai-center right-8"
          renderInnerContent={() => (
            <Button
              clear={!validEmail}
              disabled={!validEmail || savingCast || invitingCast || !!greenroomProfile || !email?.length}
              design={validEmail ? 'LocalBttnSecHalfTransp' : undefined}
              borderRadius="100px"
              designSize="small"
              className={`flex pi-center ${validEmail ? '' : 'pointer-events-none'}`}
              width={`calc(${validEmail ? '97px' : '66px'} + 8px + 16px)`}
              onClick={() => setOpenInviteModal(true)}
            >
              <Text
                whiteSpace="nowrap"
                design={validEmail ? undefined : 'GlobalIcoText3'}
                width="100%"
                className={`${validEmail ? 'L2' : 'P2'} text-center`}
              >
                {validEmail ? (greenroomProfile ? 'Invite sent' : 'Send invite') : 'optional'}
              </Text>
            </Button>
          )}
        />
      </label>
    );
  }
);

EditEmail.displayName = 'EditEmail';

export default EditEmail;
